import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Mail,
  Phone,
  MessageCircle,
  MapPin,
  Clock,
  PackageCheck,
  Truck,
  ArrowRight,
  CheckCircle2,
} from "lucide-react";
import PageHero from "@/components/site/PageHero";

const channels = [
  {
    id: "email",
    icon: Mail,
    label: "Email",
    value: "hello@freightxchange.co.za",
    href: "mailto:hello@freightxchange.co.za?subject=FreightXchange%20enquiry",
    note: "Replies within 1 hour, Mon–Fri",
  },
  {
    id: "phone",
    icon: Phone,
    label: "Phone",
    value: "+27 11 000 0000",
    href: "tel:+27110000000",
    note: "07:00 – 18:00 SAST",
  },
  {
    id: "whatsapp",
    icon: MessageCircle,
    label: "WhatsApp",
    value: "Chat with us now",
    href: "https://wa.me/27000000000",
    note: "Quotes & quick questions",
  },
  {
    id: "address",
    icon: MapPin,
    label: "Office",
    value: "Gauteng, South Africa",
    href: "#",
    note: "Servicing all SADC routes",
  },
];

const audiences = [
  {
    id: "shippers",
    icon: PackageCheck,
    title: "I've got a load to move",
    desc: "Tell us about your origin, destination, weight and dates — we'll come back with a rate within the hour.",
    cta: "Send load brief",
    href: "mailto:hello@freightxchange.co.za?subject=Load%20brief",
    accent: "bg-[#0070C0] hover:bg-[#002050] text-white",
  },
  {
    id: "carriers",
    icon: Truck,
    title: "I run trucks — work with me",
    desc: "Join our vetted carrier network. Share your fleet, compliance docs and preferred lanes and we'll be in touch.",
    cta: "Apply to join",
    href: "mailto:carriers@freightxchange.co.za?subject=Carrier%20application",
    accent: "bg-white hover:bg-[#F1F5F9] text-[#002050] border-2 border-[#E2E8F0] hover:border-[#0070C0]",
  },
];

export default function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    type: "shipper",
    message: "",
  });

  const onChange = (e) =>
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const onSubmit = (e) => {
    e.preventDefault();
    // Compose a mail draft so the message can be sent without a backend.
    const body = encodeURIComponent(
      `Name: ${form.name}\nCompany: ${form.company}\nEmail: ${form.email}\nPhone: ${form.phone}\nI am a: ${form.type}\n\n${form.message}`
    );
    window.location.href = `mailto:hello@freightxchange.co.za?subject=Website%20enquiry%20from%20${encodeURIComponent(
      form.name || "Website"
    )}&body=${body}`;
    setSubmitted(true);
  };

  return (
    <>
      <PageHero
        eyebrow="Contact us"
        badge={<><MessageCircle className="h-3.5 w-3.5" /> Let's move something</>}
        title="One phone call away from the right"
        accent="truck."
        description="Tell us about your load, your fleet or your business — we reply fast. Phone, email or WhatsApp; whichever's easiest for you."
      />

      {/* Channels */}
      <section className="relative pb-12">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {channels.map((c, i) => {
              const Icon = c.icon;
              const Comp = c.href === "#" ? "div" : "a";
              return (
                <motion.div
                  key={c.id}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.45, delay: i * 0.06 }}
                >
                  <Comp
                    href={c.href !== "#" ? c.href : undefined}
                    target={c.id === "whatsapp" ? "_blank" : undefined}
                    rel={c.id === "whatsapp" ? "noreferrer" : undefined}
                    data-testid={`contact-channel-${c.id}`}
                    className="block bg-white rounded-2xl border border-[#E2E8F0] p-6 hover:border-[#0070C0]/40 hover:-translate-y-1 hover:shadow-[0_20px_50px_-25px_rgba(0,32,80,0.25)] transition-all h-full"
                  >
                    <div className="flex items-center justify-between">
                      <div className="h-11 w-11 rounded-xl bg-[#0070C0]/10 flex items-center justify-center">
                        <Icon className="h-5 w-5 text-[#0070C0]" strokeWidth={1.75} />
                      </div>
                      <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#0070C0]">
                        {c.label}
                      </span>
                    </div>
                    <div className="mt-5 font-display text-lg font-bold text-[#002050] leading-tight break-words">
                      {c.value}
                    </div>
                    <div className="mt-1 text-xs text-[#1F2937]/65">{c.note}</div>
                  </Comp>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Form + Audience */}
      <section className="relative py-16 md:py-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-10 lg:gap-14">
          {/* Form */}
          <div className="lg:col-span-7">
            <div className="text-xs uppercase tracking-[0.24em] font-bold text-[#0070C0]">
              Send us a brief
            </div>
            <h2 className="font-display mt-3 text-3xl md:text-4xl font-extrabold tracking-tighter text-[#002050] leading-[1.05]">
              Two minutes to fill in, one hour to a reply.
            </h2>
            <p className="mt-4 text-[#1F2937]/75 leading-relaxed">
              Drop us a quick note — we'll come back with the rate, the truck
              and the timeline. No portals, no logins.
            </p>

            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4 bg-white rounded-2xl border border-[#E2E8F0] p-6 md:p-8 shadow-[0_20px_60px_-30px_rgba(0,32,80,0.18)]"
            >
              <Input
                label="Your name"
                name="name"
                required
                value={form.name}
                onChange={onChange}
                testId="contact-name"
              />
              <Input
                label="Company"
                name="company"
                value={form.company}
                onChange={onChange}
                testId="contact-company"
              />
              <Input
                label="Email"
                name="email"
                type="email"
                required
                value={form.email}
                onChange={onChange}
                testId="contact-email"
              />
              <Input
                label="Phone (optional)"
                name="phone"
                value={form.phone}
                onChange={onChange}
                testId="contact-phone"
              />
              <div className="sm:col-span-2">
                <label className="text-xs font-bold uppercase tracking-[0.18em] text-[#0070C0]">
                  I am a
                </label>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  {["shipper", "carrier"].map((t) => (
                    <label
                      key={t}
                      className={`cursor-pointer rounded-xl border-2 px-4 py-3 text-sm font-semibold transition-colors ${
                        form.type === t
                          ? "border-[#0070C0] bg-[#0070C0]/5 text-[#002050]"
                          : "border-[#E2E8F0] text-[#1F2937]/70 hover:border-[#0070C0]/40"
                      }`}
                    >
                      <input
                        type="radio"
                        name="type"
                        value={t}
                        checked={form.type === t}
                        onChange={onChange}
                        className="sr-only"
                        data-testid={`contact-type-${t}`}
                      />
                      {t === "shipper" ? "Shipper · I have a load" : "Carrier · I have trucks"}
                    </label>
                  ))}
                </div>
              </div>

              <div className="sm:col-span-2">
                <label className="text-xs font-bold uppercase tracking-[0.18em] text-[#0070C0]">
                  Tell us more
                </label>
                <textarea
                  name="message"
                  required
                  rows={5}
                  value={form.message}
                  onChange={onChange}
                  data-testid="contact-message"
                  placeholder="Origin, destination, dates, load type, weight — whatever you can share."
                  className="mt-2 w-full rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] px-4 py-3 text-[15px] text-[#1F2937] placeholder:text-[#1F2937]/40 focus:outline-none focus:border-[#0070C0] focus:bg-white transition-colors resize-none"
                />
              </div>

              <div className="sm:col-span-2 flex flex-wrap items-center justify-between gap-3 pt-2">
                <div className="flex items-center gap-2 text-xs text-[#1F2937]/60">
                  <Clock className="h-4 w-4 text-[#0070C0]" />
                  Typical reply time: under an hour
                </div>
                <button
                  type="submit"
                  data-testid="contact-submit"
                  className="group inline-flex items-center gap-2 bg-[#0070C0] hover:bg-[#002050] text-white px-7 py-3.5 rounded-xl font-bold text-sm transition-colors shadow-[0_8px_24px_rgba(0,112,192,0.35)] hover:-translate-y-0.5"
                >
                  Send brief
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </button>
              </div>

              {submitted && (
                <div className="sm:col-span-2 flex items-center gap-2 text-sm text-[#0070C0] font-semibold">
                  <CheckCircle2 className="h-4 w-4" />
                  Opening your email — we'll be in touch shortly.
                </div>
              )}
            </form>
          </div>

          {/* Audience cards */}
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.24em] font-bold text-[#0070C0]">
              Skip the form
            </div>
            <h2 className="font-display mt-3 text-3xl md:text-4xl font-extrabold tracking-tighter text-[#002050] leading-[1.05]">
              Email us directly.
            </h2>
            <p className="mt-4 text-[#1F2937]/75 leading-relaxed">
              Whichever side of the freight chain you're on, there's a direct
              line for you.
            </p>

            <div className="mt-8 flex flex-col gap-4">
              {audiences.map((a) => {
                const Icon = a.icon;
                return (
                  <a
                    key={a.id}
                    href={a.href}
                    data-testid={`contact-audience-${a.id}`}
                    className="group block rounded-2xl border border-[#E2E8F0] bg-white p-6 hover:border-[#0070C0]/40 hover:shadow-[0_20px_50px_-25px_rgba(0,32,80,0.25)] transition-all"
                  >
                    <div className="flex items-start gap-4">
                      <div className="h-12 w-12 rounded-xl bg-[#0070C0]/10 group-hover:bg-[#0070C0] flex items-center justify-center shrink-0 transition-colors">
                        <Icon className="h-5 w-5 text-[#0070C0] group-hover:text-white transition-colors" strokeWidth={1.75} />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-display text-lg md:text-xl font-bold text-[#002050]">
                          {a.title}
                        </h3>
                        <p className="mt-1.5 text-[14.5px] text-[#1F2937]/75 leading-relaxed">
                          {a.desc}
                        </p>
                        <div className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-[#0070C0]">
                          {a.cta}
                          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                        </div>
                      </div>
                    </div>
                  </a>
                );
              })}
            </div>

            {/* Hours */}
            <div className="mt-8 rounded-2xl bg-[#002050] text-white p-6">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-bold text-[#00AEEF]">
                <Clock className="h-3.5 w-3.5" /> Office hours
              </div>
              <div className="mt-3 space-y-1.5 text-sm text-white/85">
                <div className="flex justify-between">
                  <span>Mon – Fri</span>
                  <span className="font-semibold">07:00 – 18:00</span>
                </div>
                <div className="flex justify-between">
                  <span>Sat</span>
                  <span className="font-semibold">08:00 – 13:00</span>
                </div>
                <div className="flex justify-between">
                  <span>Sun &amp; public holidays</span>
                  <span className="font-semibold">On-call only</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Helper / dispatch desk */}
      <section className="pb-20 md:pb-28">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="rounded-3xl border border-[#E2E8F0] bg-[#F8FAFC] p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-xl bg-[#0070C0] flex items-center justify-center">
                <Phone className="h-5 w-5 text-white" strokeWidth={2} />
              </div>
              <div>
                <div className="font-display text-xl font-bold text-[#002050]">
                  After-hours dispatch desk
                </div>
                <div className="text-sm text-[#1F2937]/70">
                  In-transit issues? Call us 24/7.
                </div>
              </div>
            </div>
            <a
              href="tel:+27110000000"
              className="inline-flex items-center gap-2 bg-[#002050] hover:bg-[#0070C0] text-white px-6 py-3 rounded-xl font-bold text-sm transition-colors"
            >
              <Phone className="h-4 w-4" />
              +27 11 000 0000
            </a>
          </div>
        </div>
      </section>
    </>
  );
}

function Input({ label, name, type = "text", required, value, onChange, testId }) {
  return (
    <div>
      <label htmlFor={name} className="text-xs font-bold uppercase tracking-[0.18em] text-[#0070C0]">
        {label}
        {required && <span className="text-[#00AEEF]"> *</span>}
      </label>
      <input
        id={name}
        type={type}
        name={name}
        required={required}
        value={value}
        onChange={onChange}
        data-testid={testId}
        className="mt-2 w-full rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] px-4 py-3 text-[15px] text-[#1F2937] focus:outline-none focus:border-[#0070C0] focus:bg-white transition-colors"
      />
    </div>
  );
}
